import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import { CHUNK_FACTORIES } from "@/utils/routePrefetch";
import { useBranding } from "@/hooks/useBranding";

// Route-level code splitting — every non-login page is a separate chunk.
// The same factory is also used by the sidebar hover-prefetch (see routePrefetch.js).
const Dashboard      = lazy(CHUNK_FACTORIES.dashboard);
const PackagesList   = lazy(CHUNK_FACTORIES.packages);
const PackageEditor  = lazy(CHUNK_FACTORIES.package_editor);
const Vendors        = lazy(CHUNK_FACTORIES.vendors);
const Hotels         = lazy(CHUNK_FACTORIES.hotels);
const Reports        = lazy(CHUNK_FACTORIES.reports);
const Settings       = lazy(CHUNK_FACTORIES.settings);
const Users          = lazy(CHUNK_FACTORIES.users);
const AuditLog       = lazy(CHUNK_FACTORIES.audit_log);
const Customers      = lazy(CHUNK_FACTORIES.customers);
const QuickQuote     = lazy(CHUNK_FACTORIES.quick_quote);
const Kanban         = lazy(CHUNK_FACTORIES.pipeline);
const Templates      = lazy(CHUNK_FACTORIES.templates);
const Referrers      = lazy(CHUNK_FACTORIES.referrers);
const ReferrerDashboard = lazy(CHUNK_FACTORIES.my_referrals);
const MasterData     = lazy(CHUNK_FACTORIES.master_data);

const RouteSpinner = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
    <div className="flex items-center gap-3 text-muted-foreground">
      <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
      <span className="text-sm">Loading…</span>
    </div>
  </div>
);

// Wraps a route element in Suspense so lazy chunks show a spinner while loading.
const Lazy = (Element, menuKey) => {
  const inner = (
    <Suspense fallback={<RouteSpinner />}>
      <Element />
    </Suspense>
  );
  return menuKey ? <ProtectedRoute menuKey={menuKey}>{inner}</ProtectedRoute> : inner;
};

// Sends referrers to their dashboard; everyone else to the operational dashboard.
const HomeRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "referrer") return <Navigate to="/my-referrals" replace />;
  return Lazy(Dashboard, "dashboard");
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrandedApp />
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

// Applies global branding (CSS vars + favicon) on mount and re-applies when settings change.
const BrandedApp = () => {
  useBranding();
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index                    element={<HomeRedirect />} />
            <Route path="packages"          element={Lazy(PackagesList, "packages")} />
            <Route path="pipeline"          element={Lazy(Kanban,       "pipeline")} />
            <Route path="templates"         element={Lazy(Templates,    "templates")} />
            {/* Package editor is not gated by menu — anyone allowed at /packages can edit. */}
            <Route path="packages/new"      element={Lazy(PackageEditor, "packages")} />
            <Route path="packages/new/:type" element={Lazy(PackageEditor, "packages")} />
            <Route path="packages/:id"      element={Lazy(PackageEditor, "packages")} />
            <Route path="quick-quote"       element={Lazy(QuickQuote,   "quick_quote")} />
            <Route path="customers"         element={Lazy(Customers,    "customers")} />
            <Route path="vendors"           element={Lazy(Vendors,      "vendors")} />
            <Route path="hotels"            element={Lazy(Hotels,       "hotels")} />
            <Route path="reports"           element={Lazy(Reports,      "reports")} />
            <Route path="settings"          element={Lazy(Settings,     "settings")} />
            <Route path="users"             element={Lazy(Users,        "users")} />
            <Route path="audit-log"         element={Lazy(AuditLog,     "audit_log")} />
            <Route path="referrers"         element={Lazy(Referrers,    "referrers")} />
            <Route path="my-referrals"      element={Lazy(ReferrerDashboard, "my_referrals")} />
            <Route path="master-data"       element={Lazy(MasterData,   "master_data")} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </>
  );
};

export default App;
